<script setup>
import {
  ref,
  watch,
  nextTick,
  onBeforeMount,
  onMounted,
  onUnmounted,
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

import allowedShowDevFeatures from "@/services/allowedShowDevFeatures";
import { computed } from "vue";
import Clients from "@/components/icons/Clients.vue";
import ChartBar from "@/components/icons/ChartBar.vue";
import CalendarCheck from "@/components/icons/CalendarCheck.vue";
import Dashboard from "@/components/icons/Dashboard.vue";
import CalendarAlt from "@/components/icons/CalendarAlt.vue";
import Spa from "@/components/icons/Spa.vue";
import ButtonApp from "@/components/common/other/ButtonApp.vue";
import Xmark from "@/components/icons/Xmark.vue";
import Bars from "@/components/icons/Bars.vue";
import { useElementSize } from "@vueuse/core";
import { useUiStore } from "@/storePinia/ui";
import { useCompanyStore } from "@/storePinia/company";
import { storeToRefs } from "pinia";
import SackDollar from "@/components/icons/SackDollar.vue";
import TooltipApp from "@/components/TooltipApp.vue";
import api from "@/services/api";

defineProps(["textWhite"]);

const store = useStore();
const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const companyStore = useCompanyStore();
const { isSidebarOpen } = storeToRefs(uiStore);

const navbarShell = ref(null);
const { height: navbarHeight } = useElementSize(
  navbarShell,
  { width: 0, height: 0 },
  { box: "border-box" },
);

watch(navbarHeight, (value) => {
  if (!value) {
    document.documentElement.style.removeProperty("--header-height");
    return;
  }

  document.documentElement.style.setProperty("--header-height", `${value}px`);
});

onUnmounted(() => {
  document.documentElement.style.removeProperty("--header-height");
});

const showDevFeatures = ref(false);
const hasActiveSpaResource = ref(false);

const backUrl = ref(null);
const mobileIndicatorStyle = ref(null);
const links = computed(() => {
  const items = [
    {
      icon: CalendarAlt,
      to: "/ecommerce/calendar",
      text: "Календар",
      visibleIn: ["header", "mobile-menu"],
    },
  ];

  if (hasActiveSpaResource.value) {
    items.push({
      icon: Spa,
      to: "/spa/calendar",
      text: "SPA",
      visibleIn: ["header", "mobile-menu"],
    });
  }

  items.push(
    {
      icon: Dashboard,
      to: "/dashboard",
      text: "Dashboard",
      visibleIn: ["header"],
    },
    {
      icon: CalendarCheck,
      to: "/service-orders",
      text: "Броні",
      visibleIn: ["header", "mobile-menu"],
    },
    {
      icon: Clients,
      to: "/clients",
      text: "Клієнти",
      visibleIn: ["header", "mobile-menu"],
    },
    {
      icon: ChartBar,
      to: "/analytics",
      text: "Аналітика",
      visibleIn: ["header", "mobile-menu"],
    },
    {
      icon: SackDollar,
      to: "/finances/cash",
      text: "P&L",
      visibleIn: ["header"],
    },
  );

  return items;
});

const mobileActions = ref(null);

let intervalId = null;
let lastWindowWidth = null;

function updateBackUrl() {
  backUrl.value = router.options.history.state.back;
}

function updateMobileIndicator() {
  if (window.innerWidth >= 1025) {
    mobileIndicatorStyle.value = null;
    return;
  }

  const activeTab = mobileActions.value?.querySelector(
    ".navbar-main-mobile-action--active",
  );

  if (!activeTab) {
    mobileIndicatorStyle.value = null;
    return;
  }

  mobileIndicatorStyle.value = {
    width: `${activeTab.offsetWidth}px`,
    transform: `translateX(${activeTab.offsetLeft}px)`,
  };
}

function toggleNavigationOnMobile() {
  const currentWidth = window.innerWidth;
  const widthChanged = lastWindowWidth !== currentWidth;

  lastWindowWidth = currentWidth;

  if (widthChanged && currentWidth < 1200) {
    isSidebarOpen.value = false;
  }
}

async function goBack() {
  if (backUrl.value) {
    await router.go(-1);
  }
}

function toggleSidebar() {
  uiStore.toggleSidebar();
}

onBeforeMount(() => {
  showDevFeatures.value = allowedShowDevFeatures();
});

async function loadSpaResources() {
  try {
    const resources = await api.spaResources.list({ isActive: 1 });
    const list = Array.isArray(resources) ? resources : resources?.data || [];
    hasActiveSpaResource.value = list.some(
      (item) => item.isActive && item.isRemoved !== true,
    );
  } catch (error) {
    console.error("Failed to load SPA resources for navbar", error);
    hasActiveSpaResource.value = false;
  }
}

loadSpaResources();

updateBackUrl();

onMounted(() => {
  lastWindowWidth = window.innerWidth;

  window.addEventListener("resize", updateMobileIndicator);
  window.addEventListener("resize", toggleNavigationOnMobile);

  nextTick(() => {
    updateMobileIndicator();
  });
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }

  window.removeEventListener("resize", updateMobileIndicator);
  window.removeEventListener("resize", toggleNavigationOnMobile);
});

watch(route, () => {
  updateBackUrl();
  nextTick(() => {
    updateMobileIndicator();
  });
});
</script>

<template>
  <nav
    id="navbarBlur"
    ref="navbarShell"
    :class="store.state.isAbsolute ? 'mt-4' : 'mt-0'"
    class="shadow navbar navbar-main navbar-expand-lg border-radius-x sticky-top bg-white d-block navbar-main-shell"
    data-scroll="true"
    v-bind="$attrs"
  >
    <slot name="banner" />

    <div class="navbar-main__content">
      <button
        v-if="backUrl"
        :class="['back-button back-button-floating d-md-none']"
        @click="goBack"
      >
        <span class="material-icons-round navbar-main-mobile-action__icon"
          >arrow_back_ios_new</span
        >
      </button>

      <div
        class="container-fluid px-0 row w-100 flex-grow-1 navbar-main__panel d-none d-2lg-flex"
      >
        <div class="col-4 navbar-main__slot">
          <button
            :class="[
              'back-button back-button-desktop btn btn-icon btn-sm btn-outline-secondary mb-0 d-lg-none',
              { disabled: !backUrl },
            ]"
            @click="goBack"
          >
            <span class="material-icons-round back-button-desktop__icon"
              >arrow_back</span
            >
          </button>

          <div
            id="navbar"
            :class="store.state.isRTL ? 'px-0' : ''"
            class="collapse show align-items-center d-none d-2lg-flex"
          >
            <TooltipApp placement="right" text="Меню">
              <template #trigger="{ props: triggerProps }">
                <ButtonApp
                  aria-label="Меню"
                  v-bind="triggerProps"
                  variant="tertiary"
                  class="sidebar-toggle"
                  @click="uiStore.toggleSidebar"
                >
                  <template #icon>
                    <Xmark
                      v-show="isSidebarOpen"
                      class="sidebar-toggle__icon"
                    />
                    <Bars
                      v-show="!isSidebarOpen"
                      class="sidebar-toggle__icon"
                    />
                    <span
                      v-if="
                        !isSidebarOpen && companyStore.pendingInvoicesCount > 0
                      "
                      class="sidebar-toggle__badge"
                    >
                      {{ companyStore.pendingInvoicesCount }}
                    </span>
                  </template>
                </ButtonApp>
              </template>
            </TooltipApp>
          </div>
        </div>

        <div class="col-4 navbar-main__slot">
          <div class="d-2lg-flex d-none justify-content-center gap-2">
            <template v-for="link of links" :key="link.to">
              <RouterLink :to="link.to" class="navbar-main__link">
                <TooltipApp :text="link.text" placement="bottom">
                  <template #trigger="{ props: tooltipProps }">
                    <ButtonApp
                      v-bind="tooltipProps"
                      variant="tertiary"
                      class="navbar-main__link-btn"
                    >
                      <template #icon>
                        <Component
                          :is="link.icon"
                          class="navbar-main__link-icon"
                        />
                      </template>
                    </ButtonApp>
                  </template>
                </TooltipApp>
              </RouterLink>
            </template>
          </div>
        </div>
        <div class="col-4 navbar-main__slot"></div>
      </div>

      <div class="container-fluid navbar-main__mobile-panel d-2lg-none">
        <div ref="mobileActions" class="navbar-main__mobile-actions">
          <div
            v-if="mobileIndicatorStyle"
            class="navbar-main__mobile-indicator"
            :style="mobileIndicatorStyle"
          ></div>

          <template v-for="link of links" :key="link.to">
            <RouterLink
              v-if="link.visibleIn.includes('mobile-menu')"
              class="navbar-main-mobile-action"
              active-class="navbar-main-mobile-action--active"
              :to="link.to"
            >
              <Component
                :is="link.icon"
                class="navbar-main-mobile-action__icon"
              />
              <span class="navbar-main-mobile-action__label">
                {{ link.text }}
              </span>
            </RouterLink>
          </template>

          <button
            id="iconNavbarSidenav"
            type="button"
            class="navbar-main-mobile-action"
            @click.prevent.stop="toggleSidebar"
          >
            <span class="navbar-main-mobile-action__icon-wrap">
              <Xmark
                v-if="isSidebarOpen"
                class="navbar-main-mobile-action__icon"
              />
              <Bars v-else class="navbar-main-mobile-action__icon" />

              <span
                v-if="!isSidebarOpen && companyStore.pendingInvoicesCount > 0"
                class="navbar-main-mobile-action__badge"
              >
                {{ companyStore.pendingInvoicesCount }}
              </span>
            </span>

            <span
              class="navbar-main-mobile-action__label"
              style="margin-top: 5px"
              >Меню</span
            >
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.navbar {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.navbar-main__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-inline: 1rem;

  @media (min-width: 1025px) {
    min-height: var(--header-min-height);
    padding-block: 6px;
  }
}

svg.sidebar-toggle__icon {
  width: 24px;
  height: 24px;
}

.sidebar-toggle {
  position: relative;
}
.sidebar-toggle__badge {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  background: #ea0606;
  color: #fff;
  border: 1.5px solid #fff;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  line-height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.navbar-main__link.active .navbar-main__link-btn {
  background: #deede3;
  border: 1px solid #66a184;
}

.navbar-main__link-btn {
  padding: 5px;
}
.navbar-main__link-icon {
  --button-icon-size: 16px;
}
.navText {
  color: #7c8098;
}
.navbar-main__icon {
  color: #7c8098;
}
@media screen and (max-width: 767px) {
  .mobile-hidden {
    display: none;
  }
}
</style>

<style scoped>
.back-button.disabled {
  opacity: 0.3;
  pointer-events: none;
}

.back-button-desktop {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 10px;
}

.back-button-desktop__icon {
  font-size: 18px;
  line-height: 18px;
}

.navbar-main-mobile-action {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: inherit;
  text-decoration: none;
  border: none;
  background: transparent;
  box-shadow: none;
}

.navbar-main-mobile-action__icon {
  color: inherit;
  width: 21px;
  height: 21px;
  transition: color 0.2s ease;
}

.navbar-main-mobile-action__icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.navbar-main-mobile-action__badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  background: #ea0606;
  color: #fff;
  border: 1.5px solid #fff;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  line-height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.navbar-main-mobile-action__label {
  color: inherit;
}

@media screen and (max-width: 1024.98px) {
  .navbar-main-shell {
    box-shadow: none !important;
    background: transparent !important;
    border-radius: 0 !important;
  }

  .navbar-main__mobile-panel {
    position: fixed;
    left: 8px;
    width: calc(100% - 16px);
    right: 8px;
    bottom: calc(var(--mobile-bottom-nav-gap) + env(safe-area-inset-bottom));
    min-height: var(--mobile-bottom-nav-height);
    z-index: 1050;
    margin: 0;
    background: #fff;
    box-shadow: 0 -3px 5px rgba(0, 0, 0, 0.06);
    border-top: 1px solid #ececec;
    border-radius: 32px 32px 32px 32px;
    padding: 5px 12px;
  }

  .navbar-main__mobile-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0;
    width: 100%;
    position: relative;
  }

  .navbar-main__mobile-indicator {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    border-radius: 22px;
    background: #e8e9eb;
    transition:
      transform 0.24s ease,
      width 0.24s ease;
    pointer-events: none;
  }

  .navbar-main-mobile-action {
    width: auto;
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 52px;
    padding: 8px 0;
    border-radius: 20px;
    color: #000;
    transition: color 0.2s ease;
    position: relative;
    z-index: 1;
  }

  .navbar-main-mobile-action--active {
    color: #387459;
  }

  .navbar-main-mobile-action__label {
    margin-top: 2px;
    font-size: 10px;
    color: inherit;
    line-height: 1;
    font-weight: 400;
    text-transform: capitalize;
    text-align: center;
  }

  .back-button-floating {
    position: fixed;
    display: flex;
    top: calc(10px + var(--header-height));
    left: 10px;
    z-index: 1060;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #585858;
    background: rgba(255, 255, 255, 0.65);
    border: 1px solid rgba(255, 255, 255, 0.35);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(16px) saturate(180%);
    -webkit-backdrop-filter: blur(16px) saturate(180%);
    transition:
      background 0.2s,
      box-shadow 0.2s;
    overflow: hidden;
  }

  .back-button-floating .navbar-main-mobile-action__icon {
    font-size: 20px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
  }

  .back-button-floating .navbar-main-mobile-action__label {
    display: none;
  }

  .back-button-floating:hover,
  .back-button-floating:focus {
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.5);
  }

  .back-button:hover,
  .back-button:focus {
    color: inherit;
    border: none;
    background: transparent;
    box-shadow: none;
  }
}
</style>
